import * as React from 'react'
import {
	Box,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	ThemeProvider,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'

import { getSideNavBarTheme } from '../../themes'
import ProjectNavigationDropdown from '../core/ProjectNavigationDropdown'
import { useDecodeToken } from '../../security/useDecodeToken'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import { usePermissionContext } from '../../security/PermissionContext'
import { filterDrawerRoutesForProjectTemplate } from './navigationUtils'

const SCAFFOLD_ORDERS_DRAWER_PERMISSIONS = [68, 69, 78, 80]
const SCAFFOLD_ORDERS_HIDDEN_GRANT_PERMISSION = 37

export default function BrockSideBar() {
	const token = useDecodeToken()
	const filterRole = token?.role ?? 'Laborer' // least priveleged
	const { projectRoles, projectCrafts, projectTemplate } = useProjectFeaturesContext()
	const { getPermissionBasedRoutes, getProjectRoutes, hasPermission, hasAnyPermissions } =
		usePermissionContext()
	const projectRoutes = getProjectRoutes(projectRoles ?? [], projectCrafts ?? [])

	// filter routes based on permissions (preserve projectRoutes / routeData order)
	const roleRoutes = projectRoutes.filter((x) => x.roles.includes(filterRole))
	const permissionRoutes = getPermissionBasedRoutes(roleRoutes, projectRoutes)
	const allowedRouteSet = new Set([...roleRoutes, ...permissionRoutes])
	const allowedRoutes = projectRoutes.filter((r) => allowedRouteSet.has(r))
	const drawerRoutes = allowedRoutes.filter((r) => {
		if (r.title !== 'Scaffold Orders') return true
		// Special case: permission 37 should enable the route but not show it in the drawer.
		return !(
			hasPermission(SCAFFOLD_ORDERS_HIDDEN_GRANT_PERMISSION) &&
			!hasAnyPermissions(SCAFFOLD_ORDERS_DRAWER_PERMISSIONS)
		)
	})
	const filteredDrawerRoutes = filterDrawerRoutesForProjectTemplate(drawerRoutes, projectTemplate)
	// Same title can appear twice when a role has a list-only entry and permissions grant the full Projects entry.
	const seenTitles = new Set<string>()
	const uniqueDrawerRoutes = filteredDrawerRoutes.filter((route) => {
		if (seenTitles.has(route.title)) return false
		seenTitles.add(route.title)
		return true
	})
	const navigate = useNavigate()
	return (
		<Box>
			<ThemeProvider theme={getSideNavBarTheme}>
				<List>
					<>
						{uniqueDrawerRoutes.map((routeInfo) => (
							<ListItem key={routeInfo.title} sx={{ display: 'contents' }}>
								{routeInfo.title !== 'Projects' && (
									<ListItemButton
										sx={{ width: '100%', pr: 0 }}
										onClick={() => navigate(routeInfo.path)}
									>
										<ListItemIcon>{routeInfo.icon}</ListItemIcon>
										<ListItemText
											primary={
												routeInfo.path === '/equipment' && projectTemplate === 1
													? 'Contract Charges'
													: routeInfo.title
											}
										/>
									</ListItemButton>
								)}
								<ProjectNavigationDropdown routeInfo={routeInfo}></ProjectNavigationDropdown>
							</ListItem>
						))}
					</>
				</List>
			</ThemeProvider>
		</Box>
	)
}
